/**
 * Economic News Marker primitive for Lightweight Charts v5.
 * Draws colored triangles on the time axis for economic events.
 * Click to show event details.
 */

const IMPACT_COLORS = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#6b7280',
  holiday: '#8b5cf6',
};

const MARKER_SIZE = 6;

class NewsRenderer {
  constructor(data) {
    this._data = data;
  }
  draw(target) {
    const { markers } = this._data;
    if (!markers || markers.length === 0) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const hr = scope.horizontalPixelRatio;
      const vr = scope.verticalPixelRatio;
      const h = scope.bitmapSize.height;
      const bottomY = h - 4 * vr; // near bottom of chart

      for (const m of markers) {
        const x = m.x * hr;
        const size = MARKER_SIZE * vr;

        // Draw triangle pointing up
        ctx.fillStyle = m.color;
        ctx.beginPath();
        ctx.moveTo(x, bottomY - size);
        ctx.lineTo(x - size * 0.6, bottomY);
        ctx.lineTo(x + size * 0.6, bottomY);
        ctx.closePath();
        ctx.fill();

        // High impact: add glow
        if (m.impact === 'high') {
          ctx.fillStyle = m.color.replace(')', ', 0.2)').replace('rgb', 'rgba');
          ctx.beginPath();
          ctx.arc(x, bottomY - size / 2, size * 1.2, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });
  }
  drawBackground() {}
}

class NewsPaneView {
  constructor(source) {
    this._source = source;
    this._data = { markers: [] };
  }

  update() {
    const chart = this._source._chart;
    const events = this._source._events;

    if (!chart || !events || events.length === 0) {
      this._data = { markers: [] };
      return;
    }

    const timeScale = chart.timeScale();
    const markers = [];

    for (const evt of events) {
      const x = timeScale.timeToCoordinate(evt.time);
      if (x === null) continue;

      markers.push({
        x,
        color: IMPACT_COLORS[evt.impact] || IMPACT_COLORS.low,
        impact: evt.impact,
        time: evt.time,
      });
    }

    this._data = { markers };
  }

  renderer() {
    return new NewsRenderer(this._data);
  }

  zOrder() {
    return 'top';
  }
}

export class NewsMarkerPrimitive {
  constructor() {
    this._chart = null;
    this._series = null;
    this._events = [];
    this._paneView = new NewsPaneView(this);
    this._requestUpdate = null;
  }

  attached({ chart, series, requestUpdate }) {
    this._chart = chart;
    this._series = series;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._series = null;
    this._requestUpdate = null;
  }

  paneViews() {
    return [this._paneView];
  }

  updateAllViews() {
    this._paneView.update();
  }

  /**
   * @param {Array} events - [{time (unix), impact, event, currency, actual, forecast, previous}]
   */
  setData(events) {
    this._events = events;
    if (this._requestUpdate) this._requestUpdate();
  }
}
